import React, { useEffect, useRef } from 'react';

// Palette
const GOLD = { r: 240, g: 163, b: 28 };
const BLUE = { r: 82, g: 146, b: 244 };
const GREY = { r: 155, g: 157, b: 168 };

type Col = { r: number; g: number; b: number };
const C = (col: Col, a: number) =>
  `rgba(${col.r},${col.g},${col.b},${Math.max(0, Math.min(1, a))})`;
const rand = (lo: number, hi: number) => Math.random() * (hi - lo) + lo;
const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
const eO3 = (t: number) => 1 - Math.pow(1 - t, 3);
const eI3 = (t: number) => t * t * t;
const ease3 = (t: number) =>
  t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;

// Safe roundRect
function rrect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
) {
  const clampR = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + clampR, y);
  ctx.lineTo(x + w - clampR, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + clampR);
  ctx.lineTo(x + w, y + h - clampR);
  ctx.quadraticCurveTo(x + w, y + h, x + w - clampR, y + h);
  ctx.lineTo(x + clampR, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - clampR);
  ctx.lineTo(x, y + clampR);
  ctx.quadraticCurveTo(x, y, x + clampR, y);
  ctx.closePath();
}

// Timing
const DUR = {
  fadeIn: 2.5,
  scatter: 3.5,
  converge: 5.0,
  work: 5.0,
  output: 3.5,
  fadeOut: 2.5,
  black: 0.8,
};
const TOTAL = Object.values(DUR).reduce((s, v) => s + v, 0);
const ORDER = [
  'fadeIn',
  'scatter',
  'converge',
  'work',
  'output',
  'fadeOut',
  'black',
] as const;
type PhaseName = (typeof ORDER)[number];

function getPhase(elapsed: number): { name: PhaseName; p: number } {
  const ct = elapsed % TOTAL;
  let acc = 0;
  for (const name of ORDER) {
    const d = DUR[name];
    if (ct < acc + d) return { name, p: (ct - acc) / d };
    acc += d;
  }
  return { name: 'black', p: 1 };
}

function gAlpha(name: PhaseName, p: number) {
  if (name === 'fadeIn') return eO3(p);
  if (name === 'fadeOut') return 1 - eI3(p);
  if (name === 'black') return 0;
  return 1;
}

// Rings: 4 + 8 + 14 = 26 nodes
type NKind = 'gold' | 'blue' | 'grey';
interface Node {
  laneY: number;
  startX: number;
  speed: number;
  orbAngle: number;
  orbRadius: number;
  orbSpeed: number;
  kind: NKind;
  r: number;
}

const RINGS = [
  { count: 4, rFrac: 0.09, dir: 1, spd: 0.3 },
  { count: 8, rFrac: 0.18, dir: -1, spd: 0.18 },
  { count: 14, rFrac: 0.29, dir: 1, spd: 0.11 },
] as const;
const NLANES = 4;

// Konteks icon
function drawIcon(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  size: number,
  a: number,
) {
  if (a < 0.005) return;
  const col = `rgba(255,152,0,${a})`;

  const s = size / 55;
  const nW = 59 * s;
  const nH = 55 * s;
  const ox = cx - nW / 2;
  const oy = cy - nH / 2;

  ctx.save();
  ctx.translate(ox, oy);
  ctx.scale(s, s);
  ctx.fillStyle = col;

  ctx.beginPath();
  ctx.rect(0, 0, 33, 55);
  ctx.fill();
  ctx.fillStyle = 'rgba(10,10,10,0.95)';
  ctx.beginPath();
  ctx.rect(6.6, 6.548, 19.8, 41.904);
  ctx.fill();

  ctx.fillStyle = col;
  ctx.beginPath();
  ctx.rect(0, 0, 59, 31);
  ctx.fill();
  ctx.fillStyle = 'rgba(10,10,10,0.95)';
  ctx.beginPath();
  ctx.rect(6.556, 6.458, 45.889, 18.083);
  ctx.fill();

  ctx.fillStyle = col;
  ctx.beginPath();
  ctx.arc(46, 44, 7, 0, Math.PI * 2);
  ctx.fill();

  ctx.restore();
}

// Window box
const WIN_CONTENT: Record<string, string[]> = {
  left: [
    'GET  /api/v2/catalog',
    'Authorization: Bearer ...',
    '',
    '// Response 200 OK',
    '{',
    '  "total": 142,',
    '  "services": [',
    '    { "id": "bs-go-api",',
    '      "status": "stable" }',
    '  ]',
    '}',
  ],
  right: [
    'POST /api/v2/register',
    'Content-Type: application/json',
    '',
    '// Payload',
    '{',
    '  "name": "payment-svc",',
    '  "owner": "platform-team",',
    '  "lifecycle": "production"',
    '}',
    '',
    '// 201 Created',
  ],
};

function drawWindow(
  ctx: CanvasRenderingContext2D,
  wcx: number,
  wcy: number,
  w: number,
  h: number,
  a: number,
  side: 'left' | 'right',
) {
  if (a < 0.005) return;
  const r = 6,
    bx = wcx - w / 2,
    by = wcy - h / 2;

  ctx.beginPath();
  rrect(ctx, bx, by, w, h, r);
  ctx.fillStyle = `rgba(13,13,17,${a * 0.92})`;
  ctx.fill();

  ctx.beginPath();
  rrect(ctx, bx, by, w, h, r);
  ctx.strokeStyle = `rgba(60,60,70,${a * 0.8})`;
  ctx.lineWidth = 0.8;
  ctx.stroke();

  const tb = 22;
  ctx.beginPath();
  ctx.moveTo(bx + r, by);
  ctx.lineTo(bx + w - r, by);
  ctx.quadraticCurveTo(bx + w, by, bx + w, by + r);
  ctx.lineTo(bx + w, by + tb);
  ctx.lineTo(bx, by + tb);
  ctx.lineTo(bx, by + r);
  ctx.quadraticCurveTo(bx, by, bx + r, by);
  ctx.closePath();
  ctx.fillStyle = `rgba(22,22,28,${a * 0.98})`;
  ctx.fill();

  [
    `rgba(239,68,68,${a * 0.65})`,
    `rgba(245,158,11,${a * 0.65})`,
    `rgba(34,197,94,${a * 0.65})`,
  ].forEach((c, i) => {
    ctx.beginPath();
    ctx.arc(bx + 9 + i * 11, by + tb / 2, 2.8, 0, Math.PI * 2);
    ctx.fillStyle = c;
    ctx.fill();
  });

  ctx.save();
  ctx.font = `${a < 1 ? Math.round(a * 9) : 9}px monospace`;
  ctx.fillStyle = `rgba(120,120,140,${a * 0.7})`;
  ctx.fillText(
    side === 'left' ? 'catalog.http' : 'register.http',
    bx + 42,
    by + tb / 2 + 3.5,
  );
  ctx.restore();

  const lines = WIN_CONTENT[side];
  const lineH = 11.5;
  const padX = 10;
  const startY = by + tb + 10;

  ctx.save();
  ctx.font = '9px monospace';

  lines.forEach((line, li) => {
    const ly = startY + li * lineH;
    if (ly + lineH > by + h - 4) return;

    let col: string;
    if (line.startsWith('GET') || line.startsWith('POST')) {
      col = `rgba(240,163,28,${a * 0.9})`;
    } else if (
      line.startsWith('Authorization') ||
      line.startsWith('Content-Type')
    ) {
      col = `rgba(82,146,244,${a * 0.8})`;
    } else if (line.startsWith('//')) {
      col = `rgba(100,160,100,${a * 0.75})`;
    } else if (line.includes('"status"') || line.includes('"lifecycle"')) {
      col = `rgba(240,163,28,${a * 0.75})`;
    } else if (
      line.startsWith('{') ||
      line.startsWith('}') ||
      line.startsWith(']')
    ) {
      col = `rgba(180,180,200,${a * 0.65})`;
    } else if (line === '') {
      return;
    } else {
      col = `rgba(155,157,168,${a * 0.6})`;
    }

    ctx.fillStyle = col;
    ctx.fillText(line, bx + padX, ly);
  });
  ctx.restore();
}

export const FlowCanvas = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rafRef = useRef<number>(0);
  const aliveRef = useRef<boolean>(true);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    aliveRef.current = true;
    let W = 0,
      H = 0;
    let nodes: Node[] = [];
    const sxAcc: number[] = [];
    let startTime = -1;

    function build() {
      nodes = [];
      sxAcc.length = 0;
      RINGS.forEach((ring, ri) => {
        for (let ni = 0; ni < ring.count; ni++) {
          const rng = Math.random();
          const kind: NKind =
            ri === 0
              ? rng < 0.55
                ? 'gold'
                : 'blue'
              : rng < 0.15
              ? 'gold'
              : rng < 0.28
              ? 'blue'
              : 'grey';
          const nodeR =
            kind === 'gold'
              ? rand(3, 5)
              : kind === 'blue'
              ? rand(2, 3.8)
              : rand(1.8, 2.8);
          const li = ni % NLANES;
          const laneY = H * (0.28 + (li / (NLANES - 1)) * 0.44);
          nodes.push({
            laneY,
            startX: rand(-W * 0.1, W * 1.1),
            speed: rand(28, 55),
            orbAngle: (Math.PI * 2 * ni) / ring.count,
            orbRadius: Math.min(W, H) * ring.rFrac,
            orbSpeed: ring.dir * ring.spd * rand(0.88, 1.12),
            kind,
            r: nodeR,
          });
          sxAcc.push(nodes[nodes.length - 1].startX);
        }
      });
    }

    function init() {
      const dpr = window.devicePixelRatio || 1;
      W = canvas!.offsetWidth;
      H = canvas!.offsetHeight;
      canvas!.width = W * dpr;
      canvas!.height = H * dpr;
      ctx!.setTransform(dpr, 0, 0, dpr, 0, 0);
      build();
    }

    const ro = new ResizeObserver(init);
    ro.observe(canvas);
    init();

    function frame(ts: number) {
      if (!aliveRef.current) return;
      if (startTime < 0) startTime = ts;
      const elapsed = (ts - startTime) * 0.001;
      const { name, p } = getPhase(elapsed);
      const gA = gAlpha(name, p);
      const cx = W / 2,
        cy = H / 2;
      const md = Math.min(W, H);

      if (W === 0 || H === 0) {
        rafRef.current = requestAnimationFrame(frame);
        return;
      }

      const orgT =
        name === 'fadeIn' || name === 'scatter'
          ? 0
          : name === 'converge'
          ? ease3(p)
          : 1;

      if (name === 'fadeIn' || name === 'scatter' || name === 'converge') {
        nodes.forEach((n, i) => {
          sxAcc[i] += n.speed / 60;
          if (sxAcc[i] > W * 1.1) sxAcc[i] = -W * 0.1;
        });
      }

      try {
        ctx!.clearRect(0, 0, W, H);

        // Lane dashed lines
        const lA =
          name === 'fadeIn'
            ? gA * 0.55
            : name === 'scatter'
            ? gA
            : name === 'converge'
            ? (1 - ease3(p)) * gA
            : 0;
        if (lA > 0.01) {
          for (let li = 0; li < NLANES; li++) {
            const ly = H * (0.28 + (li / (NLANES - 1)) * 0.44);
            ctx!.save();
            ctx!.setLineDash([5, 9]);
            ctx!.beginPath();
            ctx!.moveTo(W * 0.02, ly);
            ctx!.lineTo(W * 0.98, ly);
            ctx!.strokeStyle = C(GREY, lA * 0.22);
            ctx!.lineWidth = 0.75;
            ctx!.stroke();
            ctx!.restore();
          }
        }

        // Orbital ring guides
        const rA =
          name === 'converge'
            ? ease3(p)
            : name === 'work'
            ? 1
            : name === 'output'
            ? Math.max(0, 1 - ease3(p) * 1.5)
            : 0;
        if (rA > 0.005) {
          RINGS.forEach((ring, ri) => {
            const ringR = md * ring.rFrac;
            ctx!.save();
            ctx!.setLineDash(ri === 0 ? [4, 7] : ri === 1 ? [5, 10] : [6, 14]);
            ctx!.beginPath();
            ctx!.arc(cx, cy, ringR, 0, Math.PI * 2);
            ctx!.strokeStyle = C(
              GOLD,
              rA * gA * (ri === 0 ? 0.6 : ri === 1 ? 0.4 : 0.26),
            );
            ctx!.lineWidth = ri === 0 ? 1.0 : 0.75;
            ctx!.stroke();
            ctx!.restore();
          });
        }

        // Konteks icon at center
        const iconA =
          name === 'converge'
            ? ease3(Math.min(p / 0.3, 1)) * gA
            : name === 'work'
            ? gA
            : name === 'output'
            ? (1 - ease3(p)) * gA
            : 0;
        if (iconA > 0.01) {
          const scale = name === 'converge' ? lerp(0.45, 1, ease3(p)) : 1;
          drawIcon(ctx!, cx, cy, md * 0.055 * scale, iconA);
        }

        // Connections
        const connA =
          name === 'converge'
            ? ease3(p) * 0.22 * gA
            : name === 'work'
            ? 0.22 * gA
            : name === 'output'
            ? (1 - ease3(p)) * 0.1 * gA
            : 0;
        if (connA > 0.005) {
          const CD = md * 0.22;
          for (let i = 0; i < nodes.length; i++) {
            const ni = nodes[i];
            const oa = ni.orbAngle + ni.orbSpeed * elapsed;
            const nix = lerp(sxAcc[i], cx + Math.cos(oa) * ni.orbRadius, orgT);
            const niy = lerp(ni.laneY, cy + Math.sin(oa) * ni.orbRadius, orgT);
            for (let j = i + 1; j < nodes.length; j++) {
              const nj = nodes[j];
              const oa2 = nj.orbAngle + nj.orbSpeed * elapsed;
              const njx = lerp(
                sxAcc[j],
                cx + Math.cos(oa2) * nj.orbRadius,
                orgT,
              );
              const njy = lerp(
                nj.laneY,
                cy + Math.sin(oa2) * nj.orbRadius,
                orgT,
              );
              const dx = njx - nix,
                dy = njy - niy,
                d = Math.sqrt(dx * dx + dy * dy);
              if (d < CD && d > 1) {
                const prox = 1 - d / CD;
                const lineCol =
                  ni.kind === 'gold' || nj.kind === 'gold'
                    ? GOLD
                    : ni.kind === 'blue' || nj.kind === 'blue'
                    ? BLUE
                    : GREY;
                ctx!.beginPath();
                ctx!.moveTo(nix, niy);
                ctx!.lineTo(njx, njy);
                ctx!.strokeStyle = C(lineCol, connA * prox * prox * 0.5);
                ctx!.lineWidth = 0.45;
                ctx!.stroke();
              }
            }
          }
        }

        // Nodes
        nodes.forEach((n, i) => {
          const oa = n.orbAngle + n.orbSpeed * elapsed;
          const nx = lerp(sxAcc[i], cx + Math.cos(oa) * n.orbRadius, orgT);
          const ny = lerp(n.laneY, cy + Math.sin(oa) * n.orbRadius, orgT);
          if (nx < -20 || nx > W + 20 || ny < -20 || ny > H + 20) return;
          const col =
            n.kind === 'gold' ? GOLD : n.kind === 'blue' ? BLUE : GREY;
          if (n.kind === 'grey') {
            ctx!.beginPath();
            ctx!.arc(nx, ny, n.r, 0, Math.PI * 2);
            ctx!.strokeStyle = C(col, gA * 0.55);
            ctx!.lineWidth = 0.85;
            ctx!.stroke();
          } else {
            ctx!.beginPath();
            ctx!.arc(nx, ny, n.r, 0, Math.PI * 2);
            ctx!.fillStyle = C(col, gA * 0.82);
            ctx!.fill();
          }
        });

        // Pulses on rings
        if (name === 'work' && p > 0.2) {
          const pa = ease3((p - 0.2) / 0.8) * gA * 0.9;
          RINGS.forEach((ring, ri) => {
            const a = elapsed * ring.dir * ring.spd * 3.5 + ri * 2.3;
            const ringR = md * ring.rFrac;
            const col = ri === 0 ? GOLD : ri === 1 ? BLUE : GREY;
            ctx!.beginPath();
            ctx!.arc(
              cx + Math.cos(a) * ringR,
              cy + Math.sin(a) * ringR,
              2.2,
              0,
              Math.PI * 2,
            );
            ctx!.fillStyle = C(col, pa);
            ctx!.fill();
          });
        }

        // Window boxes
        if (name === 'output' || (name === 'fadeOut' && p < 0.5)) {
          const bt = name === 'output' ? p : Math.max(0, 1 - p * 2);
          [
            {
              wcx: 0.3,
              wcy: 0.5,
              w: 0.24,
              h: 0.34,
              delay: 0.0,
              side: 'left' as const,
            },
            {
              wcx: 0.7,
              wcy: 0.5,
              w: 0.24,
              h: 0.34,
              delay: 0.12,
              side: 'right' as const,
            },
          ].forEach(wb => {
            const t = Math.max(0, (bt - wb.delay) / (1 - wb.delay));
            drawWindow(
              ctx!,
              wb.wcx * W,
              wb.wcy * H,
              wb.w * W,
              wb.h * H,
              ease3(t) * gA * 0.88,
              wb.side,
            );
          });
        }

        // Vignette
        const vg = ctx!.createRadialGradient(cx, cy, H * 0.1, cx, cy, H * 0.82);
        vg.addColorStop(0, 'rgba(0,0,0,0)');
        vg.addColorStop(1, 'rgba(0,0,0,0.55)');
        ctx!.fillStyle = vg;
        ctx!.fillRect(0, 0, W, H);

        // Fade overlay
        if (gA < 0.999) {
          ctx!.fillStyle = `rgba(10,10,10,${1 - gA})`;
          ctx!.fillRect(0, 0, W, H);
        }
      } catch (_) {
        // ignore
      }

      rafRef.current = requestAnimationFrame(frame);
    }

    rafRef.current = requestAnimationFrame(frame);
    return () => {
      aliveRef.current = false;
      cancelAnimationFrame(rafRef.current);
      ro.disconnect();
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        display: 'block',
      }}
    />
  );
};

export default FlowCanvas;
